/**
 * Pure logic for strategy tab management.
 * Extracted from useStrategyTabs for testability.
 */

import { EXPIRY_ANCHOR_MS_PARAM } from "@/lib/options/leg-premium";
import { strategyBaseName, type NamingLabels } from "@/lib/strategy/naming";
import { deepEqual } from "@/lib/utils";
import { isUnnamed } from "@/lib/uuid";
import type { StrategyLayer } from "@/types/strategy";
import type { Template } from "@/types/template";

// ── Types ──

export type DisplayTab = {
  id: string;
  name: string;
  ticker: string;
  draft: boolean;
  /** Raw dirty flag — drives the close-warning gate. True for any tab with
   * `scenario.has_changes`, including the freshly-anchored pending state. */
  has_changes: boolean;
  /** Display-only "show pink dot" flag — true only when the template has
   * a real clean snapshot to revert to. Suppresses the indicator for the
   * freshly-anchored state (saved_state.layers === []) where there's no
   * meaningful "saved version" diverged from. */
  dirty: boolean;
};

export type SavedTab = {
  id: string;
  name: string;
  ticker: string;
  archived: boolean;
  has_changes: boolean;
  order: number;
};

// ── Change detection ──

const LEG_OFF_KEY_RE = /^leg_off_(\d+)$/;

/**
 * Project a layer to the fields that count as user-driven template changes.
 * Strips visibility-only flags — `layer.enabled` and any `params.leg_off_N`
 * keys — so checkbox-style hide/show toggles don't dirty the template.
 * Color, label, kind, and every other param (pivot, d_leg_N, t_leg_N,
 * s_leg_N, skew, width, …) all count.
 */
function projectLayerForChanges(
  layer: StrategyLayer,
): Omit<StrategyLayer, "enabled"> {
  const { enabled: _enabled, params, ...rest } = layer;
  const filteredParams: Record<string, number> = {};
  for (const k in params) {
    if (!LEG_OFF_KEY_RE.test(k) && k !== EXPIRY_ANCHOR_MS_PARAM) {
      filteredParams[k] = params[k];
    }
  }
  return { ...rest, params: filteredParams };
}

/**
 * Equality comparator that ignores visibility flags. Use for has_changes /
 * saved_state decisions. For "did the persisted row diverge from memory?"
 * use plain `deepEqual` — visibility changes still need to be written to
 * disk, they just shouldn't flip the dirty indicator.
 */
export function layersEqualIgnoringVisibility(
  a: StrategyLayer[],
  b: StrategyLayer[],
): boolean {
  return deepEqual(
    a.map(projectLayerForChanges),
    b.map(projectLayerForChanges),
  );
}

// ── Pseudo-draft detection ──

/**
 * A "freshly-anchored" template has been promoted out of draft (a real price
 * was anchored), but the user hasn't explicitly saved it yet. It carries
 * `has_changes: true` with `saved_state.layers === []` to encode "no clean
 * snapshot — discard means delete." Treat it as a pseudo-draft: no pink dot,
 * no revert, no Save Current/Save As New options, hidden from My Strategies.
 */
export function isFreshlyAnchored(template: Template): boolean {
  const s = template.scenario;
  return (
    !s.draft &&
    template.layers.length > 0 &&
    (!template.saved_strategy_id ||
      ((s.has_changes ?? false) && (s.saved_state?.layers?.length ?? 0) === 0))
  );
}

// ── Ordering ──

/** Return the next order value (max + 1) for a new tab. Works across all templates (active + archived). */
export function nextOrder(templates: Template[]): number {
  let max = 0;
  for (const t of templates) {
    const o = t.scenario.order ?? 0;
    if (o > max) max = o;
  }
  return max + 1;
}

// ── Tab resolution ──

/** Determine which tab should be active given current state. */
export function resolveActiveTab(
  explicitId: string | null,
  loading: boolean,
  templates: Template[],
): string | null {
  if (explicitId) return explicitId;
  if (loading || templates.length === 0) return null;
  return templates[templates.length - 1].id;
}

// ── Display data ──

/**
 * The display name for an unnamed template, before dedup. Empty-layer tabs
 * keep the historical "New Tab" copy; tabs with layers fall through to the
 * auto-namer so the user sees "Iron Fly" / "Iron Fly + Bull Put Spread"
 * instead of a UUID or a generic placeholder.
 */
function unnamedBaseName(template: Template, labels: NamingLabels): string {
  if (template.layers.length === 0) return labels.newTab;
  return strategyBaseName(template.layers, labels);
}

/**
 * Resolve a template's display name. Named templates render their stored
 * name verbatim. Unnamed templates (empty string, or a legacy UUID-shaped
 * placeholder) resolve to the auto-namer's name; when multiple unnamed
 * templates share the same auto-name, suffix positionally with "(N)".
 */
export function resolveTabName(
  template: Template,
  pool: Template[],
  labels: NamingLabels,
): string {
  if (!isUnnamed(template.name)) return template.name;
  const baseName = unnamedBaseName(template, labels);
  const sameBase = pool.filter(
    (t) => isUnnamed(t.name) && unnamedBaseName(t, labels) === baseName,
  );
  if (sameBase.length <= 1) return baseName;
  const idx = sameBase.findIndex((t) => t.id === template.id);
  return `${baseName} (${idx + 1})`;
}

/**
 * Resolve the display name for any template (named or not) — the canonical
 * funnel for components that need to show or compare a single template's name.
 */
export function resolveTemplateDisplayName(
  template: Template,
  allTemplates: Template[],
  labels: NamingLabels,
): string {
  return resolveTabName(template, allTemplates, labels);
}

/** Sort templates by scenario.order (ascending), falling back to created_at. */
export function sortByOrder<T extends Template>(templates: T[]): T[] {
  return [...templates].sort(
    (a, b) => (a.scenario.order ?? 0) - (b.scenario.order ?? 0),
  );
}

/** Build display tab list from templates, sorted by order. */
export function computeDisplayTabs(
  templates: Template[],
  loading: boolean,
  labels: NamingLabels,
): DisplayTab[] {
  if (loading) return [];
  return sortByOrder(templates).map((t) => {
    const has_changes = t.scenario.has_changes ?? false;
    const dirty =
      has_changes && (t.scenario.saved_state?.layers?.length ?? 0) > 0;
    return {
      id: t.id,
      name: resolveTabName(t, templates, labels),
      ticker: t.ticker,
      draft: t.scenario.draft,
      has_changes,
      dirty,
    };
  });
}

/** Build saved tabs list: open tabs sorted by order first, then archived sorted alphabetically. */
export function computeSavedTabs(
  templates: Template[],
  archived: Template[],
  labels: NamingLabels,
): SavedTab[] {
  const pool = [...templates, ...archived];
  const open: SavedTab[] = templates
    .filter(
      (t) =>
        Boolean(t.saved_strategy_id) &&
        !t.scenario.draft &&
        !isFreshlyAnchored(t),
    )
    .map((t) => ({
      id: t.id,
      name: resolveTabName(t, pool, labels),
      ticker: t.ticker,
      archived: false,
      has_changes: t.scenario.has_changes ?? false,
      order: t.scenario.order ?? 0,
    }));
  open.sort((a, b) => a.order - b.order);

  const arch: SavedTab[] = archived
    .filter(
      (t) =>
        Boolean(t.saved_strategy_id) &&
        !t.scenario.draft &&
        !isFreshlyAnchored(t),
    )
    .map((t) => ({
      id: t.id,
      name: resolveTabName(t, pool, labels),
      ticker: t.ticker,
      archived: true,
      has_changes: false,
      order: t.scenario.order ?? 0,
    }));
  arch.sort((a, b) => a.name.localeCompare(b.name));

  return [...open, ...arch];
}

// ── Tab navigation ──

/** Determine which tab to activate after removing one. Returns null if no tabs remain. */
export function computeNextTab(
  removedId: string,
  activeId: string | null,
  templates: Template[],
): string | null {
  // Removing a non-active tab doesn't change selection
  if (removedId !== activeId) return activeId;
  const idx = templates.findIndex((t) => t.id === removedId);
  const remaining = templates.filter((t) => t.id !== removedId);
  if (remaining.length === 0) return null;
  return remaining[Math.min(idx, remaining.length - 1)].id;
}

/** Compute duplicate name: "{name} (N)" starting at 2. Duplicating an unnamed
 * template keeps the duplicate unnamed — the display layer applies its own
 * "(N)" suffix off the auto-resolved name. */
export function computeDuplicateName(
  baseName: string,
  templates: Template[],
): string {
  if (isUnnamed(baseName)) return "";
  const pattern = new RegExp(
    `^${baseName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")} \\(\\d+\\)$`,
  );
  const existing = templates.filter(
    (t) => t.name === baseName || t.name.match(pattern),
  );
  return `${baseName} (${existing.length + 1})`;
}

// ── Auto-save logic ──
